import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { useGetRegimeComparison } from 'api/generated/aid-simulation-regimes/aid-simulation-regimes';
import type { RegimeCellMetricsBucketDto } from 'api/generated/schemas';

import {
  buildRangeKey,
  formatRangeLabel,
  isNonAidRange,
} from '../definition-tab.constants';
import type { Range } from '../definition-tab.constants';

export interface ComparisonColumn {
  key: string;
  label: string;
  bucket?: RegimeCellMetricsBucketDto;
}

export interface UseRegimeComparisonResult {
  isLoading: boolean;
  isRefreshing: boolean;
  isReady: boolean;
  columns: ComparisonColumn[];
}

const compareNullsFirst = (
  a: number | null | undefined,
  b: number | null | undefined
): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;

  return a - b;
};

const buildColumns = (
  ranges: Range[],
  buckets: RegimeCellMetricsBucketDto[]
): ComparisonColumn[] => {
  const bucketByRank = new Map(
    buckets.map((bucket) => [bucket.bucketRank, bucket])
  );
  const rankByRangeKey = new Map(
    ranges
      .filter((range) => !isNonAidRange(range))
      .sort(
        (a, b) =>
          compareNullsFirst(a.from, b.from) || compareNullsFirst(a.to, b.to)
      )
      .map((range, index) => [buildRangeKey(range), index + 1])
  );

  return ranges.map((range) => {
    const key = buildRangeKey(range);

    return {
      key,
      label: formatRangeLabel(range),
      bucket: isNonAidRange(range)
        ? undefined
        : bucketByRank.get(rankByRangeKey.get(key)),
    };
  });
};

export const useRegimeComparison = (
  regimeId: number | null,
  ranges: Range[]
): UseRegimeComparisonResult => {
  const { modelId: modelIdParam } = useParams();
  const modelId = modelIdParam ? Number(modelIdParam) : 0;
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.oktaUserId ?? '';

  const query = useGetRegimeComparison(userId, modelId, regimeId ?? 0, {
    query: {
      enabled: !!userId && !!modelId && !!regimeId,
      retry: false,
    },
  });

  const buckets = query.data?.needBuckets ?? [];

  const columns = useMemo(
    () => buildColumns(ranges, buckets),
    [ranges, query.data]
  );

  return {
    isLoading: query.isLoading,
    isRefreshing: query.isFetching && !query.isLoading,
    isReady: query.isSuccess && !!buckets.length,
    columns,
  };
};
